import { useEffect, useRef, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// DailyHisaab "Count Up" launch splash (ported from the Claude Design handoff).
// Logo bounces in → "TODAY'S COLLECTION" → ₹ counts up to the real value →
// coins stack → "MADE WITH LOVE IN BENGAL" + heartbeat. Plays once (4.5s).
//
// The only dynamic input is today's collection — read synchronously from the
// LOCAL Dexie cache (no network/Sheets wait). 0 / new user → ₹0 + "No sales
// yet today". Theme-aware: matches the app's light/dark mode.
// ─────────────────────────────────────────────────────────────────────────────

const DURATION = 4.5; // seconds — minimum splash time (the full animation)
const MAX_HOLD = 10; // seconds — safety cap; hold on the settled frame at most this long
const STAGE_W = 720;
const STAGE_H = 1560;
const CENTER_X = STAGE_W / 2;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const easeOutCubic = (t: number) => --t * t * t + 1;
const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

// interpolate([0, 0.2, 1.0], [0, 0, 1.0], [linear, easeOutBack]) for the logo pop-in.
function logoScaleAt(time: number): number {
  if (time <= 0.2) return 0;
  if (time >= 1.0) return 1;
  return easeOutBack((time - 0.2) / 0.8);
}

function formatINR(n: number): string {
  n = Math.max(0, Math.round(n));
  const s = String(n);
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return rest + "," + last3;
}

const HEART = "#E84F4F";
const GOLD = "#EBB236";
const GOLD_DK = "#B5841F";

const THEMES = {
  light: {
    bg: "#FAF3E6",
    num: "#A8312F",
    cap: "#6E7686",
    sub: "#6E7686",
    tag: "#A89B82",
    glow: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 62%)",
    home: "#2A3140",
    homeOp: 0.55,
  },
  dark: {
    bg: "#1A1614",
    num: "#F7F1E7",
    cap: "#9C9089",
    sub: "#8A8079",
    tag: "#8A7F73",
    glow: "radial-gradient(circle at 50% 50%, rgba(255,250,242,0.10) 0%, rgba(255,250,242,0) 62%)",
    home: "#FAF3E6",
    homeOp: 0.45,
  },
};

/** Sum today's sale totals from the local Dexie cache (instant, no network). */
async function loadTodaysCollection(): Promise<number> {
  try {
    const { db } = await import("@/lib/db");
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const sales = await db.transactions.where("type").equals("sale").toArray();
    const sum = sales.reduce((acc, t) => {
      if (t.cancelled) return acc;
      return new Date(t.createdAt) >= startOfToday ? acc + (Number(t.total) || 0) : acc;
    }, 0);
    return Math.max(0, Math.round(sum));
  } catch {
    return 0;
  }
}

export function CountUpSplash({ onDone, appReady = true }: { onDone: () => void; appReady?: boolean }) {
  const [time, setTime] = useState(0);
  const [amount, setAmount] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  const [scale, setScale] = useState(() =>
    typeof window !== "undefined" ? Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H) : 1,
  );
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const appReadyRef = useRef(appReady);
  appReadyRef.current = appReady;
  const dismissingRef = useRef(false);

  // Real today's collection — local cache, available on mount.
  useEffect(() => {
    let cancelled = false;
    loadTodaysCollection().then((v) => {
      if (!cancelled) setAmount(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the canvas scaled to the viewport.
  useEffect(() => {
    const onResize = () => setScale(Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Follow theme if it flips while mounted.
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Drive the animation; the heart keeps beating while we hold on the settled
  // frame waiting for the app to load. Dismiss (exactly once) when the animation
  // has played AND the app is ready, or after a generous safety cap. This runs
  // INSIDE the RAF so a re-render can never cancel the finish timer — which would
  // leave an invisible full-screen overlay swallowing every tap.
  useEffect(() => {
    let raf = 0;
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start == null) start = ts;
      const tt = (ts - start) / 1000;
      setTime(tt);
      if (!dismissingRef.current && ((tt >= DURATION && appReadyRef.current) || tt >= MAX_HOLD)) {
        dismissingRef.current = true;
        setFadeOut(true);
        window.setTimeout(() => onDoneRef.current(), 360);
        return; // stop the loop; the fade plays out, then onDone unmounts us
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const t = time;
  const theme = isDark ? THEMES.dark : THEMES.light;
  const hasData = amount > 0;

  // logo bounce + breathe
  const lBreath = t > 1.05 ? 1 + Math.sin((t - 1.05) * Math.PI * 0.8) * 0.012 : 1;
  const logoScale = logoScaleAt(t) * lBreath;
  const LW = 440;
  const LH = (LW * 116) / 476;
  const logoCY = 500;

  // count up
  const ct = clamp((t - 1.1) / 1.5, 0, 1);
  const val = amount * easeOutCubic(ct);
  const endPop = 1 + Math.sin(clamp((t - 2.45) / 0.35, 0, 1) * Math.PI) * 0.05;
  const capOp = clamp((t - 0.95) / 0.4, 0, 1);
  const numOp = clamp((t - 1.05) / 0.4, 0, 1);

  const numCY = 810;
  const glowOp = clamp((t - 1.0) / 0.6, 0, 1) * 0.6;

  // coins
  const coinsBaseY = 1230;
  const COIN_RX = 74;
  const COIN_RY = 17;
  const EDGE = 21;
  const coins = [0, 1, 2, 3, 4, 5];

  // tagline + heartbeat
  const tgEased = easeOutCubic(clamp((t - 2.9) / 0.7, 0, 1));
  const tgTy = (1 - tgEased) * 10;
  let hs = 1;
  if (t > 3.5) {
    const b = (t - 3.5) % 1.4;
    if (b < 0.18) hs = 1 + Math.sin((b / 0.18) * Math.PI) * 0.24;
    else if (b > 0.22 && b < 0.4) hs = 1 + Math.sin(((b - 0.22) / 0.18) * Math.PI) * 0.14;
  }

  const FONT = "Inter, system-ui, sans-serif";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        background: theme.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: fadeOut ? 0 : 1,
        transition: "opacity 350ms ease",
        pointerEvents: fadeOut ? "none" : "auto",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          width: STAGE_W,
          height: STAGE_H,
          position: "relative",
          flexShrink: 0,
          background: theme.bg,
          transform: `scale(${scale})`,
          transformOrigin: "center",
        }}
      >
        {/* soft glow behind the number */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - 320,
            top: numCY - 240,
            width: 640,
            height: 480,
            background: theme.glow,
            opacity: glowOp,
            pointerEvents: "none",
            filter: "blur(4px)",
          }}
        />

        {/* logo */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - LW / 2,
            top: logoCY - LH / 2,
            width: LW,
            height: LH,
            transform: `scale(${logoScale})`,
            transformOrigin: "center",
            willChange: "transform",
          }}
        >
          <img
            src="/dailyhisaab-splash-logo.png"
            alt="DailyHisaab"
            width={LW}
            height={LH}
            style={{ display: "block", width: "100%", height: "100%" }}
            draggable={false}
          />
        </div>

        {/* caption */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - 320,
            top: 690,
            width: 640,
            textAlign: "center",
            fontFamily: FONT,
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: "0.24em",
            color: theme.cap,
            opacity: capOp,
          }}
        >
          TODAY'S COLLECTION
        </div>

        {/* big number */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - 340,
            top: numCY - 70,
            width: 680,
            textAlign: "center",
            fontFamily: FONT,
            fontWeight: 800,
            fontSize: 116,
            lineHeight: 1,
            color: theme.num,
            opacity: numOp,
            transform: `scale(${endPop})`,
            transformOrigin: "center",
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.03em",
          }}
        >
          ₹{formatINR(val)}
        </div>

        {/* sub-line */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - 320,
            top: numCY + 70,
            width: 640,
            textAlign: "center",
            fontFamily: FONT,
            fontSize: 24,
            fontWeight: 500,
            color: theme.sub,
            opacity: capOp,
          }}
        >
          {hasData ? "so far today" : "No sales yet today"}
        </div>

        {/* coin stack */}
        <svg
          viewBox="0 0 300 360"
          width="300"
          height="360"
          style={{ position: "absolute", left: CENTER_X - 150, top: coinsBaseY - 300, pointerEvents: "none" }}
        >
          <ellipse cx="150" cy="332" rx={COIN_RX + 6} ry="8" fill="rgba(0,0,0,0.10)" />
          {coins.map((i) => {
            const thresh = 1.35 + i * 0.22;
            const ti = clamp((t - thresh) / 0.4, 0, 1);
            if (ti <= 0) return null;
            const eased = easeOutCubic(ti);
            const finalY = 320 - i * (EDGE + 5);
            const y = finalY - 120 + 120 * eased;
            const sT = clamp((t - (thresh + 0.28)) / 0.2, 0, 1);
            const sq = Math.sin(sT * Math.PI) * 0.1;
            const rx = COIN_RX * (1 + sq);
            const ry = COIN_RY * (1 - sq * 0.6);
            return (
              <g key={i}>
                <rect x={150 - rx} y={y} width={rx * 2} height={EDGE} fill={GOLD_DK} />
                <path d={`M ${150 - rx} ${y + EDGE} A ${rx} ${ry} 0 0 0 ${150 + rx} ${y + EDGE}`} fill={GOLD_DK} />
                <ellipse cx="150" cy={y} rx={rx} ry={ry} fill={GOLD} />
                <ellipse cx="150" cy={y} rx={rx - 9} ry={ry - 3} fill="none" stroke={GOLD_DK} strokeWidth="1.5" opacity="0.6" />
                <text x="150" y={y + 7} textAnchor="middle" fontFamily={FONT} fontSize="20" fontWeight="800" fill={GOLD_DK} opacity="0.85">
                  ₹
                </text>
              </g>
            );
          })}
        </svg>

        {/* tagline + heartbeat */}
        <div
          style={{
            position: "absolute",
            left: CENTER_X - 300,
            bottom: 70,
            width: 600,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            opacity: tgEased,
            transform: `translateY(${tgTy}px)`,
            fontFamily: FONT,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "0.18em",
            color: theme.tag,
          }}
        >
          <span>MADE WITH LOVE</span>
          <span
            style={{
              display: "inline-flex",
              transform: `scale(${hs})`,
              transformOrigin: "center",
              filter: hs > 1.05 ? `drop-shadow(0 0 8px ${HEART}55)` : "none",
            }}
          >
            <svg width="26" height="24" viewBox="0 0 24 22">
              <path
                d="M12 21s-7.5-4.6-9.6-9.2C.9 8.3 2.7 4.3 6.3 4c2.2-.2 4.1 1 5.7 2.9C13.6 5 15.5 3.8 17.7 4c3.6.3 5.4 4.3 3.9 7.8C19.5 16.4 12 21 12 21z"
                fill={HEART}
              />
            </svg>
          </span>
          <span>IN BENGAL</span>
        </div>

        {/* Android home indicator */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: 18,
            transform: "translateX(-50%)",
            width: 160,
            height: 6,
            borderRadius: 3,
            background: theme.home,
            opacity: theme.homeOp,
          }}
        />
      </div>
    </div>
  );
}

export default CountUpSplash;
